<template>
  <div id="notify-a-claim-page">
    <div class="flex-grow my-4">
      <h1>{{ $t('ClaimNotificationForms') }}</h1>
    </div>
    <div class="vx-card full-page">
      <grid-panel
          class="notify-a-claim-grid"
          :column-defs="columnDefs"
          :url="url"
          @download="downloadClaimNotification"
          @online="openClaimNotification"
          :apiParams="apiParams"
          :gridOptions="gridOptions"
          :autoGroupColumnDef="autoGroupColumnDef"
          :show-sidebar="false"
          :defaultColDef="defaultColDef"
          :headerTextWrap="true"
          :tooltipShowDelay="0"
      >
        <template slot="right">
          <vs-button
              color="secondary"
              class="ml-4 text-lg"
              @click="getDownloadAllFile"
          >
            {{ $t('DownloadAllClaimsNotificationsForm') }}
          </vs-button>
        </template>
      </grid-panel>
    </div>
    <download-all
        v-if="showDownloadPopup"
        :downloading="downloadingAll"
        :success="downloadAllSuccess"
        :download-file="downloadFile"
        @downloadAll="downloadAll"
        @closeDownloadAllModal="closeDownloadAllModal"
    />

    <pdf-reader
      v-model="showPdfReader"
      :pdfReaderSrc="pdfReaderSrc"
    />
  </div>
</template>

<script>
import GridPanel from '@/views/components/ag-grid/GridPanel';
import GridActionsMenu from "@/views/components/extra-components/GridActionsMenu";
import DateRangePicker from "@/views/components/calendar/DateRangePicker";
import CellRendererDate from "@/views/components/ag-grid/cell-renderer/CellRendererDate";
import CellRendererDownload from "@/views/components/ag-grid/cell-renderer/CellRendererDownload";
import CellRendererCurrency from "@/views/components/ag-grid/cell-renderer/CellRendererCurrency";
import CellRendererOnline from '@/views/components/ag-grid/cell-renderer/CellRendererOnline';
import FileDownloadMixin from "@/components/mixins/FileDownloadMixin";
import GoToLinkMixin from "@/components/mixins/GoToLinkMixin"
import SetGridOptionsMixin from "@/components/mixins/SetGridOptionsMixin";
import DownloadAll from './DownloadAll';
import ToolTipRenderer from '@/views/components/ag-grid/components/ToolTipRenderer';
import PdfReader from '@/views/components/pdfReader/PdfReader.vue';
import { TABLE_IDENTIFIER_FW_CLAIM_NOTIFICATIONS_ID } from '@/constants'

export default {
  components: {
    PdfReader,
    GridPanel,
    GridActionsMenu,
    DateRangePicker,
    CellRendererDate,
    CellRendererDownload,
    CellRendererCurrency,
    CellRendererOnline,
    DownloadAll,
    ToolTipRenderer,
  },
  mixins: [GoToLinkMixin, FileDownloadMixin, SetGridOptionsMixin],
  data() {
    return {
      customOfflineRatersEndpoint: this.$api.claimNotifications.getOfflineRaters(),
      url: (params) => this.$api.claimNotifications.list(params),
      startDate: null,
      endDate: null,
      documentTypes: null,
      usePdfPreview: true,
      gridOptions: {},
      languageApiParams: { language: this.$i18n.locale },
      autoGroupColumnDef: {
        headerName: '',
        cellRendererParams: { suppressCount: true },
        minWidth: 130,
        maxWidth: 130,
        colSpan: (params) => {
          if (!params.data) {
            return 9;
          }

          return 1;
        },
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
        wrapText: true,
        autoHeight: true,
        tooltipComponent: 'ToolTipRenderer',
      },
      showDownloadPopup: false,
      downloadingAll: false,
      downloadAllSuccess: false,
      downloadFile: {},
      tableIdentifier: TABLE_IDENTIFIER_FW_CLAIM_NOTIFICATIONS_ID,
    };
  },
  computed: {
    apiParams() {
      return this.languageApiParams;
    },
    columnDefs() {
      if (!this.documentTypes) {
        return [];
      }

      let columnDefs = [
        {
          headerName: 'Risk Class',
          field: 'RiskClass',
          rowGroup: true,
          hide: true,
        },
        {
          headerName: 'Sub Risk Class',
          field: 'SubRiskClass',
          rowGroup: true,
          hide: true,
        },
      ];

      let onlineRaterCol = {};

      const dynamicColumns = this.documentTypes
          .filter((col) => {
            if (col.Document_Type === 'Online Notication') {
              onlineRaterCol = col;
              return false;
            }

            return true;
          })
          .map(({ ID, Document_Type, DisplayName, Icon, IconType, IconSize, IconColour }) => {
            const field = 'DocumentType_' + ID;
            
            return {
              headerName: DisplayName,
              skipTranslation: true,
              field: field,
              minWidth: 120,
              sortable: false,
              resizable: true,
              headerClass: 'header-center header-text-wrap',
              cellRenderer: 'CellRendererDownload',
              cellClass: 'flex justify-center',
              cellStyle: { 'font-size': IconSize, 'color': IconColour },
              cellRendererParams(params) {
                return {
                  iconType: IconType,
                  icon: Icon,
                  documentType: 'salesDocument',
                  documentTypeId: ID,
                };
              },
            };
          });

      columnDefs.push({
        headerName: 'InsuranceSolutions',
        field: 'Product_Name',
        tooltipField: 'Product_Name',
        tooltipComponentParams: { field: 'Product_Name' },
        minWidth: 400,
        wrapText: false,
        autoHeight: false,
      });

      columnDefs.push({
        headerName: 'OnlineForm',
        tooltipField: 'OnlineForm',
        tooltipComponentParams: { field: 'Raters' },
        minWidth: 150,
        cellRenderer: 'CellRendererOnline',
        cellClass: 'flex justify-center',
        cellRendererParams(params) {
          return {
            onlineRaterCol,
          }
        },
      });

      columnDefs.push(...dynamicColumns);

      const columnDefsWithWidths = this.getColumnDefsWithWidths(columnDefs)

      return columnDefsWithWidths;
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async openClaimNotification(params) {
      try {
        const { data } = await this.$api.claimNotifications.getClaimNotificationLink({
          params: {
            claimnotificationform: params.documentGuid,
            language: this.apiParams.language,
          }
        });

        this.openUrl({
          DocumentDownloadUrl: data.data.ClaimNotificationLink,
        });

      } catch (e) {
        console.log(e);
      }
    },
    async downloadClaimNotification(params) {
      try {
        const { data } = await this.$api.claimNotifications.getClaimNotification({
          params: {
            documentTypeId: params.documentTypeId,
            documentGuid: params.documentGuid[0],
          }
        });

        this.getFile({
          DocumentDownloadUrl: data.data.DocumentDownloadUrl,
          FriendlyName: data.data.FriendlyName,
          Extension: data.data.Extension,
        });

      } catch (e) {
        console.log(e);
      }
    },
    closeDownloadAllModal() {
      this.showDownloadPopup = false;
      this.downloadingAll = false;
      this.downloadAllSuccess = false;
      this.downloadFile = {};
    },
    async fetchData() {
      await this.getDocumentTypes();
    },
    async getDocumentTypes() {
      try {
        const { data } = await this.$api.claimNotifications.getNotificationDocumentTypes({params: this.languageApiParams});
        this.documentTypes = data.data;
      } catch (e) {
        this.$vs.notify({
          title: this.$t('ErrorOccurred'),
          text: this.$t('DocumentTypesFetchFailureMessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right',
        });
      }
    },
    getCategoryLabel(category) {
      return category.RiskClass;
    },
    getSubTabs(category) {
      if (!category.Sub_RiskClasses) {
        return category
      }

      return category.Sub_RiskClasses;
    },
    handleDownloadData(data) {
      const params = data.data;
      return this.download({ params} );
    }
  }
};
</script>

<style lang="scss">

#notify-a-claim-page {
  .notify-a-claim-grid {
    .ag-row {
      background-color: white !important;

      &.ag-row-group.ag-row-level-0 {
        background-color: #e7e6e6  !important;
      }

      &.ag-row-group.ag-row-level-1 {
        background-color: #f8f8f8   !important;
      }
    }
  }
}
</style>
